class MainNav {
  private readonly navElement: Element | null
  private readonly trigger: Element | null
  private readonly menuItemsWithDropdown: NodeListOf<Element>
  private readonly allMenuItems: NodeListOf<Element>
  private readonly subItems: NodeListOf<Element>

  private currentIndex: number = 0
  private subIndex: number = 0
  private readonly keyboardMsg: Element | null
  private readonly keyCodes = {
    enter: 'Enter',
    esc: 'Escape',
    space: 'Space',
    left: 'ArrowLeft',
    up: 'ArrowUp',
    right: 'ArrowRight',
    down: 'ArrowDown',
    tab: 'Tab',
  }

  constructor() {
    this.navElement = document.querySelector('.main-navigation')
    this.trigger = document.querySelector('.js-main-nav-trigger')
    if (this.navElement == null) return

    this.allMenuItems = document.querySelectorAll('.mn-menu-item')
    this.menuItemsWithDropdown = document.querySelectorAll('.mn-menu-item[data-has-submenu="true"]')
    this.subItems = document.querySelectorAll('.mn-child-menu-item')
    this.keyboardMsg = document.querySelector('.js-keyboard-msg')

    if (this.trigger != null) this.setupNavTrigger()
    this.setupNav()
    this.setupHoverEvents()
    this.setupResizeHandler()
    this.syncNavHiddenState()
    this.syncMenuItemAriaExpanded()
  }

  private setupNavTrigger(): void {
    this.trigger?.addEventListener('keydown', this.triggerKeydownEvent.bind(this))

    if ('ontouchstart' in document.documentElement) {
      this.trigger?.addEventListener('touchstart', (event: TouchEvent) => {
        this.toggleNav()
      })
      return
    }

    this.trigger?.addEventListener('click', (event: MouseEvent) => {
      this.toggleNav()
    })
  }

  private triggerKeydownEvent(event: KeyboardEvent): void {
    if (event.code !== this.keyCodes.tab) return
    this.handleMobileTab(event)
  }

  private toggleNav(): void {
    const isOpen = !(this.trigger?.classList.contains('is-active') ?? false)
    this.setNavOpen(isOpen)
  }

  private setNavOpen(isOpen: boolean): void {
    this.navElement?.classList.toggle('visible', isOpen)
    this.trigger?.classList.toggle('is-active', isOpen)
    this.trigger?.setAttribute('aria-expanded', String(isOpen))

    this.setOverflow(isOpen)
    this.syncNavHiddenState()

    if (isOpen && this.isMobileWidth()) {
      this.focusMobileStop(this.getMobileFocusStops(), 0)
    }
  }

  private syncNavHiddenState(): void {
    const isOpen: boolean = this.trigger?.classList.contains('is-active') ?? false
    const isHidden = this.isMobileWidth() && !isOpen

    this.navElement?.setAttribute('aria-hidden', String(isHidden))

    if (isHidden) {
      this.navElement?.setAttribute('inert', '')
    } else {
      this.navElement?.removeAttribute('inert')
    }
  }

  private setupResizeHandler(): void {
    window.addEventListener('resize', () => {
      if (!this.isMobileWidth()) {
        this.setNavOpen(false)
      } else {
        this.syncNavHiddenState()
      }

      this.syncMenuItemAriaExpanded()
    })
  }

  private syncMenuItemAriaExpanded(): void {
    this.menuItemsWithDropdown.forEach((item) => {
      const button = item.querySelector<HTMLButtonElement>('.mn-menu-item-tap-area-expander')

      if (this.isMobileWidth()) {
        item.removeAttribute('aria-expanded')
        button?.removeAttribute('aria-expanded')
      } else if (!item.classList.contains('expanded')) {
        item.setAttribute('aria-expanded', 'false')
        button?.setAttribute('aria-expanded', 'false')
      }
    })
  }

  private setupHoverEvents(): void {
    if (this.menuItemsWithDropdown.length === 0) return // @ts-expect-error
    ;[...this.menuItemsWithDropdown].forEach((item: Element) => {
      item.addEventListener('mouseenter', () => {
        if (this.isMobileWidth()) return
        item.setAttribute('aria-expanded', 'true')
      })

      item.addEventListener('mouseleave', () => {
        if (this.isMobileWidth()) return
        item.setAttribute('aria-expanded', 'false')
      })
    })
  }

  private setupNav(): void {
    if (this.allMenuItems.length > 0) {
      // @ts-expect-error
      ;[...this.allMenuItems].forEach((element: Element, index: Number) => {
        element.setAttribute('tabindex', index === 0 ? '0' : '-1')

        if (index === 0) {
          element.addEventListener('focus', () => {
            this.currentIndex = 0
          })
        }

        element.addEventListener('focus', this.focusEvent.bind(this))
        element.addEventListener('click', this.clickEvent.bind(this))
        element.addEventListener('keydown', this.keydownEvent.bind(this))
      })
    }

    if (this.subItems.length > 0) {
      // @ts-expect-error
      ;[...this.subItems].forEach((element: Element) => {
        element.setAttribute('tabindex', '-1')

        element.addEventListener('keydown', this.subItemsKeydownEvent.bind(this))
      })
    }

    document.addEventListener('mousemove', this.hideKeyboardMsg.bind(this))
  }

  private focusEvent(event: FocusEvent): void {
    const currentTarget = event.currentTarget as Element
    this.showKeyboardMsg()
    this.toggleSubmenu(currentTarget)

    this.setActiveDescendant(currentTarget.id)
  }

  private isMobileWidth(): boolean {
    return window.innerWidth <= 1024
  }

  private toggleSubmenu(menuItem: Element): void {
    if (this.isMobileWidth()) return

    this.menuItemsWithDropdown.forEach((item) => {
      item.classList.remove('expanded')
      item.setAttribute('aria-expanded', 'false')
      const submenu: HTMLElement | null = item.querySelector('.mn-child-menu')
      if (submenu !== null) {
        submenu.classList.add('hidden')
        submenu.style.removeProperty('display')
      }
    })

    // Only do this on menu items that have dropdowns
    if (Array.from(this.menuItemsWithDropdown).includes(menuItem)) {
      menuItem.classList.toggle('expanded')
      menuItem.setAttribute('aria-expanded', 'true')
      const toggleButton = menuItem.querySelector<HTMLButtonElement>(
        '.mn-menu-item-tap-area-expander'
      )
      if (toggleButton != null) toggleButton.setAttribute('aria-expanded', 'true')
    }

    const currentSubmenu: HTMLElement | null = menuItem.querySelector('.mn-child-menu')
    if (currentSubmenu !== null) {
      currentSubmenu.style.display = 'block'
    }
  }

  private setActiveDescendant(focusedItemId: string): void {
    this.navElement?.setAttribute('aria-activedescendant', focusedItemId)
  }

  private clearAriaCurrent(): void {
    this.navElement?.removeAttribute('aria-activedescendant')
  }

  // Click event on top level menu items
  private clickEvent(event: Event): void {
    const target = event.target as Element

    if (target.nodeName === 'A') {
      return
    }

    if (this.isMobileWidth()) return

    const currentTarget = event.currentTarget as Element
    const isExpanded = currentTarget.getAttribute('aria-expanded')

    this.menuItemsWithDropdown.forEach((item) => {
      item.classList.remove('expanded')
      item.setAttribute('aria-expanded', 'false')
    })

    if (isExpanded === 'false' || isExpanded === null) {
      currentTarget.classList.add('expanded')
      currentTarget.setAttribute('aria-expanded', 'true')
    } else {
      currentTarget.classList.remove('expanded')
      currentTarget.setAttribute('aria-expanded', 'false')
    }
  }

  private keydownEvent(event: KeyboardEvent): void {
    const element = event.currentTarget as HTMLElement
    const submenu = element.querySelector('ul')
    let preventDefault = false

    switch (event.code) {
      case this.keyCodes.right:
        this.showKeyboardMsg()
        this.gotoIndex(this.allMenuItems, this.currentIndex + 1)
        preventDefault = true
        break
      case this.keyCodes.left:
        this.showKeyboardMsg()
        this.gotoIndex(this.allMenuItems, this.currentIndex - 1)
        preventDefault = true
        break
      case this.keyCodes.down:
        this.showKeyboardMsg()
        preventDefault = true
        if (submenu == null) return
        element.click()
        this.subIndex = 0
        this.gotoSubIndex(submenu, 0)
        break
      case this.keyCodes.up:
        this.showKeyboardMsg()
        preventDefault = true
        if (submenu === null) return
        element.click()
        this.subIndex = submenu.querySelectorAll('li').length - 1
        this.gotoSubIndex(submenu, this.subIndex)
        break
      case this.keyCodes.tab: {
        this.showKeyboardMsg()
        if (this.handleMobileTab(event)) return

        const nextIndex = this.getNextVisibleMenuItemIndex(event.shiftKey ? -1 : 1)

        if (nextIndex === -1) {
          this.hideKeyboardMsg()
          this.clearAriaCurrent()
          return
        }

        this.gotoIndex(this.allMenuItems, nextIndex)
        preventDefault = true
        break
      }
      case this.keyCodes.space:
        ;(element.querySelector('.mn-menu-item-link') as HTMLAnchorElement | null)?.click()
        preventDefault = true
        break
      case this.keyCodes.enter:
        ;(element.querySelector('.mn-menu-item-link') as HTMLAnchorElement | null)?.click()
        break
      case this.keyCodes.esc: {
        const nextElSibling = this.navElement?.nextElementSibling as HTMLElement | null

        if (nextElSibling !== null) {
          nextElSibling.setAttribute('tabindex', '-1')
          nextElSibling.focus()
        }

        preventDefault = true
        break
      }
    }

    if (preventDefault) {
      event.preventDefault()
    }
  }

  private showKeyboardMsg(): void {
    if (this.keyboardMsg != null) {
      this.keyboardMsg.classList.remove('hidden')
    }
  }

  private hideKeyboardMsg(): void {
    if (this.isMobileWidth()) return

    if (this.keyboardMsg != null) {
      this.keyboardMsg.classList.add('hidden')
    }

    this.menuItemsWithDropdown.forEach((item) => {
      if (item.matches(':hover')) return

      item.classList.remove('expanded')
      item.setAttribute('aria-expanded', 'false')
      const submenu: HTMLElement | null = item.querySelector('.mn-child-menu')
      if (submenu !== null) {
        submenu.classList.remove('hidden')
        submenu.style.removeProperty('display')
      }
    })
  }

  private gotoIndex(menuItems: NodeListOf<Element>, position: number): void {
    if (position === menuItems.length) {
      position = 0
    } else if (position < 0) {
      position = menuItems.length - 1
    }

    ;(menuItems[position] as HTMLElement).focus()
    this.currentIndex = position
  }

  private gotoSubIndex(menu: HTMLElement | null, position: number): void {
    if (menu == null) return

    const items = menu.querySelectorAll('li')

    if (position === items.length) {
      position = 0
    } else if (position < 0) {
      position = items.length - 1
    }

    items[position].querySelector('a') != null
      ? items[position].querySelector('a')?.focus()
      : items[position].focus()
    this.subIndex = position

    // Set active descendant here for sub items.
    const indexCheck = items[position].id
    this.setActiveDescendant(indexCheck)
  }

  private isMobileNavOpen(): boolean {
    return this.isMobileWidth() && (this.trigger?.classList.contains('is-active') ?? false)
  }

  private getNextVisibleMenuItemIndex(direction: 1 | -1): number {
    let idx = this.currentIndex + direction

    while (idx >= 0 && idx < this.allMenuItems.length) {
      const item = this.allMenuItems[idx] as HTMLElement
      // offsetParent is null when the element (or an ancestor) is display:none.
      if (item.offsetParent !== null || item.getClientRects().length > 0) {
        return idx
      }
      idx += direction
    }

    return -1
  }

  private getMobileFocusStops(): HTMLElement[] {
    if (this.navElement == null) return []
    const items = Array.from(
      this.navElement.querySelectorAll<HTMLElement>('.mn-menu-item, .mn-child-menu-item')
    )
    const triggerEl = this.trigger as HTMLElement | null

    return triggerEl != null ? [...items, triggerEl] : items
  }

  private focusMobileStop(stops: HTMLElement[], index: number): void {
    if (stops.length === 0) return
    if (index < 0) index = stops.length - 1
    if (index >= stops.length) index = 0

    const stop = stops[index]
    const target = stop.classList.contains('mn-child-menu-item')
      ? (stop.querySelector<HTMLElement>('a, button') ?? stop)
      : stop

    target.focus()
  }

  private handleMobileTab(event: KeyboardEvent): boolean {
    if (!this.isMobileNavOpen()) return false

    const stops = this.getMobileFocusStops()
    const active = document.activeElement as HTMLElement | null
    const current =
      active === this.trigger
        ? (this.trigger as HTMLElement)
        : (active?.closest('.mn-menu-item, .mn-child-menu-item') as HTMLElement | null)
    const currentIndex = current != null ? stops.indexOf(current) : -1

    if (currentIndex === -1) return false

    this.focusMobileStop(stops, event.shiftKey ? currentIndex - 1 : currentIndex + 1)
    event.preventDefault()
    return true
  }

  private subItemsKeydownEvent(event: KeyboardEvent): void {
    const element = event.currentTarget as HTMLLIElement
    let preventDefault = false

    switch (event.code) {
      case this.keyCodes.right:
        this.showKeyboardMsg()
        this.gotoIndex(this.allMenuItems, this.currentIndex + 1)
        preventDefault = true
        break
      case this.keyCodes.left:
        this.showKeyboardMsg()
        this.gotoIndex(this.allMenuItems, this.currentIndex - 1)
        preventDefault = true
        break
      case this.keyCodes.esc:
        this.gotoIndex(this.allMenuItems, this.currentIndex)
        preventDefault = true
        break
      case this.keyCodes.down:
        this.showKeyboardMsg()
        this.gotoSubIndex(element.parentElement, this.subIndex + 1)
        preventDefault = true
        break
      case this.keyCodes.up:
        this.showKeyboardMsg()
        if (this.subIndex === 0) {
          this.gotoIndex(this.allMenuItems, this.currentIndex)
        } else {
          this.gotoSubIndex(element.parentElement, this.subIndex - 1)
        }
        preventDefault = true
        break
      case this.keyCodes.space:
        ;(element.firstElementChild as HTMLAnchorElement).click()
        preventDefault = true
        break
      case this.keyCodes.tab:
        this.showKeyboardMsg()
        if (this.handleMobileTab(event)) {
          event.stopPropagation()
          return
        }
        break
    }

    if (preventDefault) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  private setOverflow(shouldAdd: boolean): void {
    const html = document.querySelector('html')

    if (html == null) return

    setTimeout(() => {
      html.style.overflow = document.body.style.overflow = shouldAdd ? 'hidden' : ''
    }, 100)
  }
}

export default MainNav
